import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { LiveAssistStart } from '../api/types';
import { MutationError } from '../components/MutationError';
import { useProjectApi } from '../projects/useProjectApi';
import { liveCategoryLabels, type LiveGuidelineMessage, type LiveMessage } from './liveAssist';

type Hint = { id: number; category: string; text: string };
type Line = { id: number; speaker: string; text: string };

// LiveAssistPanel: Live-Hilfe im Gespräch – erst nach Zustimmung des Kunden zur Aufzeichnung. Der Live-Dienst hört
// beide Seiten mit (Gemini, Vertex AI in Frankfurt) und schickt kurze Hinweise nach dem Gesprächsleitfaden des Projekts
// (den der Browser nach dem Verbinden übergibt); gespeichert wird nichts.
export function LiveAssistPanel({ callId, active }: { callId: string; active: boolean }) {
  const papi = useProjectApi();
  const [session, setSession] = useState<LiveAssistStart | null>(null);
  const url = session?.agent_url ?? null;
  const [status, setStatus] = useState('');
  const [hints, setHints] = useState<Hint[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const start = useMutation({ mutationFn: () => papi.startLiveAssist(callId), onSuccess: (r) => setSession(r) });
  const stop = useMutation({
    mutationFn: () => papi.stopLiveAssist(callId),
    onSuccess: () => {
      setSession(null);
      setStatus('Live-Hilfe beendet.');
    },
  });
  const guideline = session?.guideline;

  // Läuft sie schon (z. B. nach dem Neuladen), nur neu verbinden.
  const { mutate: connect } = start;
  useEffect(() => {
    if (active) connect();
  }, [active, connect]);

  useEffect(() => {
    if (!url) return;
    let seq = 0;
    const ws = new WebSocket(url);
    ws.onopen = () => {
      setStatus('Verbunden – die Hinweise erscheinen, sobald gesprochen wird.');
      if (guideline) {
        const msg: LiveGuidelineMessage = { type: 'guideline', text: guideline.text, mandatory_points: guideline.mandatory_points };
        ws.send(JSON.stringify(msg));
      }
    };
    ws.onclose = () => setStatus((s) => (s.startsWith('Verbunden') ? 'Verbindung getrennt.' : s));
    ws.onmessage = (e) => {
      let m: LiveMessage;
      try {
        m = JSON.parse(String(e.data)) as LiveMessage;
      } catch {
        return;
      }
      seq += 1;
      const id = seq;
      if (m.type === 'hint') setHints((h) => [{ id, category: m.category ?? 'INFO', text: m.text }, ...h].slice(0, 8));
      else if (m.type === 'line') setLines((l) => [...l, { id, speaker: m.speaker ?? '', text: m.text }].slice(-6));
      else if (m.type === 'status') setStatus(m.text);
    };
    return () => ws.close();
  }, [url, guideline]);

  return (
    <section className="live-assist card" aria-label="Live-Hilfe">
      <header className="live-assist-head">
        <strong>Live-Hilfe</strong>
        {url ? (
          <button type="button" className="btn btn-small" disabled={stop.isPending} onClick={() => stop.mutate()}>
            Beenden
          </button>
        ) : (
          <button type="button" className="btn btn-small btn-primary" disabled={start.isPending} onClick={() => start.mutate()}>
            Live-Hilfe starten
          </button>
        )}
      </header>
      {!url && !status && <p className="muted small">Hört beide Seiten mit und gibt Ihnen kurze Hinweise, z. B. bei Einwänden. Es wird nichts gespeichert.</p>}
      {status && (
        <p className="muted small" role="status">
          {status}
        </p>
      )}
      {(start.isError || stop.isError) && <MutationError error={start.error ?? stop.error} />}
      {hints.length > 0 && (
        <ul className="plain-list live-hints">
          {hints.map((h) => (
            <li key={h.id}>
              <span className={h.category === 'PFLICHT' ? 'badge badge-warn' : 'badge badge-info'}>{liveCategoryLabels[h.category] ?? 'Hinweis'}</span>{' '}
              {h.text}
            </li>
          ))}
        </ul>
      )}
      {lines.length > 0 && (
        <ul className="plain-list live-lines" aria-label="Mitschrift">
          {lines.map((l) => (
            <li key={l.id}>
              <span className="muted">{l.speaker === 'CUSTOMER' ? 'Kunde' : 'Sie'}:</span> {l.text}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
